import classNames from "classnames";
import React, { Component } from "react";
import styled from "styled-components";

import { t } from "js/i18n";
import WorkspaceController from "js/controllers/WorkspaceController";
import UpgradeIcon from "js/Components/UpgradeIcon";
import UpgradeSection from "js/Components/UpgradeSection";
import { FolderProfile, UserProfile } from "js/react/components/Avatar";
import { FlexBox } from "js/react/components/LayoutGrid";
import Loadable from "js/react/components/Loadable";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { ShowDialog } from "js/react/components/Dialogs/BaseDialog";
import { WorkspacePlanTier } from "common/interfaces/IWorkspacePlan";

const SelectableProfile = styled.div`
    padding: 10px 20px 10px 14px;
    width: 100%;

    &.selected {
        background: #D6EFF9;
        cursor: pointer;
    }

    .avatar.folder > i,
    .avatar.team > i {
        color: #11A9E2;
    }
`;

const NoMatch = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 350px;
    padding: 14px 30px;
    font-size: 15px;
    color: #666;
`;

const ListHeader = styled.div`
    display: flex;
    align-items: center;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-family: 'Source Sans Pro';
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 14px;
    color: #A9A9A9;
    margin: 10px;
`;

const UpgradeSectionWrapper = styled.div`
    max-width: 245px;
`;

export class InviteSuggestions extends Component {
    refListWrapper = React.createRef();

    componentDidUpdate = () => {
        if (this.refListWrapper.current) {
            const parent = this.refListWrapper.current?.ref?.current || this.refListWrapper.current;
            const selected = parent.querySelector(".selected");
            if (selected) {
                // Scroll into view next frame so React can finish rendering first
                setTimeout(() =>
                    selected.scrollIntoView({
                        block: "nearest",
                        inline: "nearest",
                        behavior: "smooth",
                    })
                );
            }
        }
    }

    handleClick = (event, item) => {
        // Prevent focus
        event.nativeEvent.stopImmediatePropagation();
        event.nativeEvent.preventDefault();

        const { inviteContext, onSelect } = this.props;

        inviteContext.selectItems([item]);
        onSelect && onSelect(item);
    }

    renderProfile = (item, indexPendingSelection, selectable = true) => {
        const { inviteContext } = this.props;

        return (
            <SelectableProfile
                key={item.id || item.email}
                className={classNames({
                    selectable,
                    selected: indexPendingSelection === inviteContext.pendingSelectionIndex,
                })}
                // We bind to mouse down instead of click so we can prevent focus
                onMouseDown={event => selectable && this.handleClick(event, item)}
                onMouseEnter={() => selectable && inviteContext.setPendingSelectionIndex(indexPendingSelection)}
            >
                {
                    (
                        item.type === "user" ||
                        item.type === "email"
                    ) &&
                    <UserProfile
                        profile={item}
                        highlight={inviteContext.getFilterWithoutCategories()}
                    />
                }
                {
                    item.type === "folder" &&
                    <FolderProfile
                        profile={item}
                        highlight={inviteContext.getFilterWithoutCategories()}
                    />
                }
            </SelectableProfile>
        );
    }

    renderNewSuggestions = () => {
        const { inviteContext } = this.props;
        const { filteredNewSuggestions, filteredOldSuggestions, oldFirst } = inviteContext;

        return (
            <>
                {
                    !!filteredNewSuggestions.length &&
                    <ListHeader>{inviteContext.labelCategoryNew}</ListHeader>
                }
                {
                    filteredNewSuggestions.map((item, index) => {
                        const indexPendingSelection = oldFirst ? filteredOldSuggestions.length + index : index;
                        return this.renderProfile(item, indexPendingSelection);
                    })
                }
            </>
        );
    }

    renderOldSuggestions = () => {
        const { inviteContext } = this.props;
        const { filteredNewSuggestions, filteredOldSuggestions, oldFirst } = inviteContext;

        return (
            <>
                {
                    !!filteredOldSuggestions.length &&
                    <ListHeader>{inviteContext.labelCategoryOld}</ListHeader>
                }
                {
                    filteredOldSuggestions.map((item, index) => {
                        const indexPendingSelection = oldFirst ? index : filteredNewSuggestions.length + index;
                        return this.renderProfile(item, indexPendingSelection);
                    })
                }
            </>
        );
    }

    renderEmptyState = () => {
        const { inviteContext, showUpgradeCTA = true } = this.props;

        if (showUpgradeCTA && !WorkspaceController.isMultiUserWorkspace) {
            return (
                <UpgradeSectionWrapper>
                    <UpgradeSection
                        title={t("Invite your team")}
                        body={t("Collaborate with your team in one click. Create Shared folders and tag collaborators when you upgrade to the Team Plan.")}
                        buttonText={t("Upgrade")}
                        buttonIcon={<UpgradeIcon />}
                        buttonOnClick={() => ShowDialog(ChangePlanDialog, {
                            featuredPlan: WorkspacePlanTier.TEAM,
                        })}
                    />
                </UpgradeSectionWrapper>
            );
        }

        if (inviteContext.filter) {
            return <NoMatch><i>{t("No matches")}</i></NoMatch>;
        }

        return <Loadable />;
    }

    renderSuggestionsContent = () => {
        const { inviteContext } = this.props;

        if (inviteContext.filteredAllSuggestions.length === 0) {
            return this.renderEmptyState();
        }

        const { oldFirst } = inviteContext;

        if (oldFirst) {
            return (
                <>
                    {this.renderOldSuggestions()}
                    {this.renderNewSuggestions()}
                </>
            );
        }

        return (
            <>
                {this.renderNewSuggestions()}
                {this.renderOldSuggestions()}
            </>
        );
    }

    render = () => {
        const { sourceName, maxHeight = "calc(30vh)" } = this.props;

        return (
            <FlexBox
                ref={this.refListWrapper}
                id="inviteSuggestions"
                className={`source-${sourceName}`}
                vertical
                left
                fillWidth
                top
                style={{ maxHeight, overflowY: "auto", position: "relative" }}
                onMouseDown={event => {
                    // Prevent closing the list if it's clicked on
                    event.stopPropagation();
                    event.preventDefault();
                }}
            >
                {this.renderSuggestionsContent()}
            </FlexBox>
        );
    }
}
